import { nativeHlsResultsSupported, videoSupportsNativeHls, type NativeHlsCapabilityEnvironment } from "@/lib/nativeHls";

export const HLS_NETWORK_RETRY_LIMIT = 8;
export const HLS_NETWORK_RESTART_GRACE_MS = 90_000;
export const HLS_BUFFERED_RETRY_THRESHOLD_SECONDS = 0.5;
export const HLS_MAX_FORWARD_BUFFER_SECONDS = 110;
const HLS_NETWORK_RETRY_BASE_DELAY_MS = 1_000;
const HLS_NETWORK_RETRY_MAX_DELAY_MS = 8_000;

type PlayerHlsBufferPolicy = {
  maxBufferLength: number;
  maxMaxBufferLength: number;
  maxBufferSize: number;
  backBufferLength: number;
};

const NORMAL_BUFFER_POLICY: PlayerHlsBufferPolicy = {
  maxBufferLength: 30,
  maxMaxBufferLength: 45,
  maxBufferSize: 60 * 1000 * 1000,
  backBufferLength: 30,
};

const CONSTRAINED_BUFFER_POLICY: PlayerHlsBufferPolicy = {
  maxBufferLength: 90,
  maxMaxBufferLength: HLS_MAX_FORWARD_BUFFER_SECONDS,
  maxBufferSize: 120 * 1000 * 1000,
  backBufferLength: 90,
};

export type FatalHlsAction =
  | { type: "retry_network"; delayMs: number }
  | { type: "recover_media" }
  | { type: "handoff_session"; message: string };

export function shouldUseNativeHls(
  canPlayTypeResults: string[],
  environment?: NativeHlsCapabilityEnvironment,
): boolean {
  return nativeHlsResultsSupported(canPlayTypeResults, environment);
}

export function shouldVideoUseNativeHls(video: HTMLVideoElement): boolean {
  return videoSupportsNativeHls(video);
}

export function playerHlsBufferPolicy(constrainedStreaming: boolean): PlayerHlsBufferPolicy {
  return constrainedStreaming ? CONSTRAINED_BUFFER_POLICY : NORMAL_BUFFER_POLICY;
}

export function buildPlayerHlsConfig(
  startLevelHint: number,
  constrainedStreaming = false,
) {
  const bufferPolicy = playerHlsBufferPolicy(constrainedStreaming);
  return {
    enableWorker: false,
    // Blocking Playlist Reload (#973): the server advertises
    // EXT-X-SERVER-CONTROL:CAN-BLOCK-RELOAD=YES on ready-window playlists and
    // holds an _HLS_msn request until that segment exists. hls.js only issues
    // that request immediately after a playlist load when lowLatencyMode is
    // on; off, it attaches the directive but waits for the target-duration
    // timer, which on a 12 s grid is exactly the boundary stall the server
    // change was meant to remove (pbs_00003802, 2026-09-18). No partial
    // segments are advertised, so nothing else about low-latency mode
    // engages: no PART-HOLD-BACK, no part loading, no playback-rate catch-up
    // (maxLiveSyncPlaybackRate stays 1).
    lowLatencyMode: true,
    // The server has already anchored the ready-only playlist at the requested
    // deterministic segment. Load its first entry; the fragment's rebased PTS
    // restores the absolute full-title media clock after append.
    startPosition: 0,
    maxBufferLength: bufferPolicy.maxBufferLength,
    maxMaxBufferLength: bufferPolicy.maxMaxBufferLength,
    maxBufferSize: bufferPolicy.maxBufferSize,
    maxBufferHole: 1,
    backBufferLength: bufferPolicy.backBufferLength,
    abrBandWidthFactor: 0.95,
    // 0.7 (hls.js default) is tuned for volatile cellular; on the relay path
    // the measurement itself is already conservative (transfer-shape effects,
    // #757), so stacking the default gate on top held sessions a full rung
    // below their estimate. Oscillation protection lives in the distinct-
    // resolution ladder and stall caps (#393/#395), not here.
    abrBandWidthUpFactor: 0.85,
    maxStarvationDelay: 4,
    maxLoadingDelay: 4,
    startLevel: startLevelHint,
    fragLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: 15_000,
        maxLoadTimeMs: 30_000,
        timeoutRetry: { maxNumRetry: 6, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
        errorRetry: { maxNumRetry: 6, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
      },
    },
    manifestLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: 15_000,
        maxLoadTimeMs: 20_000,
        timeoutRetry: { maxNumRetry: 6, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
        errorRetry: { maxNumRetry: 6, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
      },
    },
  };
}

/**
 * Native ABR policy: hls.js owns level selection (it has built-in EWMA +
 * hysteresis). We only apply a one-time conservative startup cap so playback
 * does not cold-start on the top variant; once playback is established with a
 * healthy buffer we release to fully automatic (-1) and never hand-drive
 * level switching again. Returns the value to assign to hls.autoLevelCapping
 * plus whether the startup cap ref should now be cleared.
 *
 * Replaces the previous per-tick multi-controller (starvation downshift vs
 * high-confidence upshift vs LEVEL_SWITCHING anti-downswitch pinning), which
 * fought hls.js and itself and oscillated between adjacent top variants on
 * fast connections (#393).
 */
export function resolveNativeAbrCap(options: {
  startupLevelCap: number | null;
  stallRecoveryLevelCap?: number | null;
  awaitingPlaybackStart: boolean;
  playbackStarted: boolean;
  bufferedAheadSeconds: number;
  releaseBufferSeconds: number;
}): { cap: number; releaseStartupCap: boolean } {
  const cap = options.startupLevelCap;
  const holdingStartup =
    cap !== null
    && Number.isInteger(cap)
    && cap >= 0
    && (
      options.awaitingPlaybackStart
      || !options.playbackStarted
      || !Number.isFinite(options.bufferedAheadSeconds)
      || options.bufferedAheadSeconds < options.releaseBufferSeconds
    );
  if (holdingStartup) {
    return {
      cap: combineAbrCaps(cap as number, options.stallRecoveryLevelCap ?? null),
      releaseStartupCap: false,
    };
  }
  return {
    cap: combineAbrCaps(-1, options.stallRecoveryLevelCap ?? null),
    releaseStartupCap: true,
  };
}

export function combineAbrCaps(nativeCap: number, stallRecoveryCap: number | null): number {
  if (
    stallRecoveryCap === null
    || !Number.isInteger(stallRecoveryCap)
    || stallRecoveryCap < 0
  ) {
    return nativeCap;
  }
  if (!Number.isInteger(nativeCap) || nativeCap < 0) {
    return stallRecoveryCap;
  }
  return Math.min(nativeCap, stallRecoveryCap);
}

export const STALL_CAP_RELEASE_BUFFER_SECONDS = 20;
export const STALL_CAP_RELEASE_QUIET_MS = 30_000;

// A stall-recovery cap is a ratchet: it only ever tightens, and before #667 it
// was released only when the runtime was rebuilt — one startup wobble pinned a
// session to a low rung forever while the bandwidth estimate sat far above it
// (observed: capped at `medium` with a 3.4 Mbit/s estimate). Release it once
// the buffer has been deep and stall-free long enough that the instability it
// guarded against is demonstrably over; native ABR then climbs on its own.
export function shouldReleaseStallRecoveryCap(options: {
  stallRecoveryLevelCap: number | null;
  bufferedAheadSeconds: number;
  msSinceLastStall: number;
}): boolean {
  return (
    options.stallRecoveryLevelCap !== null
    && Number.isFinite(options.bufferedAheadSeconds)
    && options.bufferedAheadSeconds >= STALL_CAP_RELEASE_BUFFER_SECONDS
    && Number.isFinite(options.msSinceLastStall)
    && options.msSinceLastStall >= STALL_CAP_RELEASE_QUIET_MS
  );
}

export function resolveStallRecoveryLevelCap(options: {
  currentLevel: number | null;
  levelsCount: number;
  existingCap: number | null;
}): number | null {
  const currentLevel = options.currentLevel;
  const existingCap = options.existingCap;
  if (
    !Number.isInteger(currentLevel)
    || currentLevel === null
    || currentLevel <= 0
  ) {
    return existingCap;
  }

  const topLevel = Number.isInteger(options.levelsCount) && options.levelsCount > 0
    ? options.levelsCount - 1
    : currentLevel;
  const drop = currentLevel >= topLevel && currentLevel >= 2 ? 2 : 1;
  const nextCap = Math.max(0, currentLevel - drop);
  if (
    existingCap !== null
    && Number.isInteger(existingCap)
    && existingCap >= 0
  ) {
    return Math.min(existingCap, nextCap);
  }
  return nextCap;
}

export const FAST_UPSHIFT_MIN_BUFFER_SECONDS = 4;
export const FAST_UPSHIFT_BANDWIDTH_MARGIN = 1.25;

// resolveFastUpshiftLevel decides whether to jump the player straight to a
// higher rung instead of letting native hls.js ABR crawl up one level at a
// time. Native ABR is correct but slow to climb on an over-provisioned link
// (it took ~26s to reach 1080p on a 14-157Mbps LAN where max is 6M, #399).
// We allow this as soon as the startup buffer target is satisfied; waiting for
// a much deeper buffer only locks in more low-quality media ahead of the playhead.
//
// It is strictly ONE-DIRECTIONAL (only ever returns a level ABOVE the current
// one) — downshifts stay with native starvation handling, so this can never
// recreate the max<->ultra oscillation (#393) on the distinct-resolution
// ladder (#395). It also clamps to autoLevelCapping, so a stall-recovery /
// down-ratchet cap is always respected (climb fast UP TO the sustainable
// ceiling, never past it).
//
// Returns the target level to command, or -1 for "no upshift".
export function resolveFastUpshiftLevel(options: {
  variantBitrates: number[];
  currentLevel: number;
  autoLevelCapping: number;
  measuredBandwidthBps: number;
  bufferedAheadSeconds: number;
  playbackStarted: boolean;
  awaitingPlaybackStart: boolean;
  buffering: boolean;
  blocked: boolean;
  minBufferSeconds?: number;
  marginFactor?: number;
}): number {
  const minBufferSeconds = options.minBufferSeconds ?? FAST_UPSHIFT_MIN_BUFFER_SECONDS;
  const marginFactor = options.marginFactor ?? FAST_UPSHIFT_BANDWIDTH_MARGIN;
  if (
    !options.playbackStarted
    || options.awaitingPlaybackStart
    || options.buffering
    || options.blocked
    || !Number.isFinite(options.bufferedAheadSeconds)
    || options.bufferedAheadSeconds < minBufferSeconds
    || !Number.isFinite(options.measuredBandwidthBps)
    || options.measuredBandwidthBps <= 0
    || options.variantBitrates.length === 0
  ) {
    return -1;
  }
  // Highest rung the measured bandwidth comfortably sustains.
  let target = -1;
  for (let i = 0; i < options.variantBitrates.length; i += 1) {
    const bitrate = options.variantBitrates[i] ?? 0;
    if (Number.isFinite(bitrate) && bitrate > 0 && bitrate * marginFactor <= options.measuredBandwidthBps) {
      target = i;
    }
  }
  if (target < 0) {
    return -1;
  }
  // Respect an active ceiling (startup / stall-recovery / down-ratchet cap).
  if (Number.isInteger(options.autoLevelCapping) && options.autoLevelCapping >= 0) {
    target = Math.min(target, options.autoLevelCapping);
  }
  // One-directional only: never command a downshift here.
  if (target <= options.currentLevel) {
    return -1;
  }
  return target;
}

export function calculateNetworkRetryDelayMs(retryCount: number): number {
  return Math.min(
    HLS_NETWORK_RETRY_BASE_DELAY_MS * 2 ** Math.max(0, retryCount - 1),
    HLS_NETWORK_RETRY_MAX_DELAY_MS,
  );
}

export function resolveFatalHlsAction(options: {
  errorType: string;
  errorDetails?: string;
  networkRetryCount: number;
  mediaRecoveryCount: number;
  networkOutageElapsedMs?: number;
  bufferedAheadSeconds?: number;
  playbackStarted?: boolean;
  networkStallHandoffAfterMs?: number;
}): FatalHlsAction {
  const activeNetworkOutageStalled = options.playbackStarted === true
    && typeof options.bufferedAheadSeconds === "number"
    && Number.isFinite(options.bufferedAheadSeconds)
    && options.bufferedAheadSeconds <= HLS_BUFFERED_RETRY_THRESHOLD_SECONDS
    && (options.networkOutageElapsedMs ?? 0) >= (options.networkStallHandoffAfterMs ?? HLS_NETWORK_RESTART_GRACE_MS);

  switch (options.errorType) {
    case "networkError":
      if (activeNetworkOutageStalled) {
        return {
          type: "handoff_session",
          message: "Playback stalled after a network interruption; reconnecting.",
        };
      }
      if (
        options.networkRetryCount < HLS_NETWORK_RETRY_LIMIT
        || (options.networkOutageElapsedMs ?? 0) < HLS_NETWORK_RESTART_GRACE_MS
        || (options.bufferedAheadSeconds ?? 0) > HLS_BUFFERED_RETRY_THRESHOLD_SECONDS
      ) {
        return {
          type: "retry_network",
          delayMs: calculateNetworkRetryDelayMs(options.networkRetryCount + 1),
        };
      }
      return {
        type: "handoff_session",
        message: "Playback stalled and could not recover automatically.",
      };
    case "mediaError":
      if (options.errorDetails === "bufferStalledError" && activeNetworkOutageStalled) {
        return {
          type: "handoff_session",
          message: "Playback stalled after a network interruption; reconnecting.",
        };
      }
      if (options.mediaRecoveryCount < 1) {
        return { type: "recover_media" };
      }
      return {
        type: "handoff_session",
        message: "Playback hit a media error and could not recover automatically.",
      };
    default:
      return {
        type: "handoff_session",
        message: "Playback failed and could not recover automatically.",
      };
  }
}
